import { useState } from "react";
import { getVerticalMeta } from "@/components/verticals/VerticalIcons";
import { useParams, Link, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  ShieldAlert,
  User,
  Bot,
  Briefcase,
  DollarSign,
  Calendar,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Loader2,
  Clock,
  FileText,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Separator } from "@/components/ui/separator";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useDisputeDetail, useResolveDispute } from "@/hooks/use-queries";
import { formatDistanceToNow } from "date-fns";
import { toast } from "sonner";

export default function DisputeDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: dispute, isLoading, error } = useDisputeDetail(id!);
  const resolveMutation = useResolveDispute();

  type ResolutionType = "PENDING_INFO" | "CLOSED_NO_ACTION" | "RESOLVED_REFUND" | "RESOLVED_PAY" | "RESOLVED_SPLIT" | "";
  const [resolution, setResolution] = useState<ResolutionType>("");
  const [resolutionNote, setResolutionNote] = useState("");
  const [infoRequestedFrom, setInfoRequestedFrom] = useState<"POSTER" | "AGENT_OWNER" | "BOTH" | "">("");
  const [infoRequestDetails, setInfoRequestDetails] = useState("");
  const [posterBps, setPosterBps] = useState<number>(5000);

  const handleResolve = async () => {
    // Validation
    if (!resolution) {
      toast.error("Please select a resolution type");
      return;
    }

    if (resolution === "PENDING_INFO") {
      if (!infoRequestedFrom || !infoRequestDetails.trim()) {
        toast.error("Please specify who should provide info and what information is needed");
        return;
      }
    } else if (resolution !== "CLOSED_NO_ACTION" && !resolutionNote.trim()) {
      toast.error("Please provide a resolution note");
      return;
    }

    try {
      const payload: any = {
        id: id!,
        status: resolution,
      };

      if (resolutionNote.trim()) {
        payload.resolutionNote = resolutionNote.trim();
      }

      if (resolution === "PENDING_INFO") {
        payload.infoRequestedFrom = infoRequestedFrom;
        payload.infoRequestDetails = infoRequestDetails.trim();
      }

      if (resolution === "RESOLVED_SPLIT") {
        payload.posterBps = posterBps;
      }

      await resolveMutation.mutateAsync(payload);

      toast.success("Dispute resolved successfully");
      navigate("/admin/disputes");
    } catch (err: any) {
      toast.error(err.response?.data?.message || "Failed to resolve dispute");
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-12 w-64" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  if (error || !dispute) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <AlertTriangle className="h-16 w-16 text-destructive mb-4" />
        <h2 className="text-2xl font-bold mb-2">Dispute Not Found</h2>
        <p className="text-muted-foreground mb-6">The dispute you're looking for doesn't exist.</p>
        <Button asChild>
          <Link to="/admin/disputes">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Disputes
          </Link>
        </Button>
      </div>
    );
  }

  const isResolved = dispute.status !== "OPEN" && dispute.status !== "PENDING_INFO";

  return (
    <div className="space-y-6 pb-10">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" asChild>
          <Link to="/admin/disputes">
            <ArrowLeft className="h-4 w-4" />
          </Link>
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-1">
            <h1 className="text-3xl font-display font-bold text-red-500">Dispute Resolution</h1>
            <Badge variant={isResolved ? "secondary" : "destructive"} className="text-xs">
              {dispute.status}
            </Badge>
          </div>
          <p className="text-sm text-muted-foreground font-mono">
            Case #{dispute.id.split('-')[0].toUpperCase()}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Job Details */}
          <Card className="glass-panel border-red-500/20">
            <CardHeader>
              <div className="flex items-start gap-3">
                <div className="h-10 w-10 rounded-full bg-red-500/10 flex items-center justify-center shrink-0">
                  <Briefcase className="h-5 w-5 text-red-500" />
                </div>
                <div className="flex-1">
                  <CardTitle className="text-xl">Job: {dispute.job.title}</CardTitle>
                  <CardDescription className="mt-1">
                    <Link
                      to={`/jobs/${dispute.job.id}`}
                      className="text-primary hover:underline inline-flex items-center gap-1"
                    >
                      View full job details
                      <ArrowLeft className="h-3 w-3 rotate-180" />
                    </Link>
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div>
                  <p className="text-xs text-muted-foreground uppercase font-bold mb-1">Budget</p>
                  <div className="flex items-center gap-1 text-lg font-bold font-mono">
                    <DollarSign className="h-4 w-4 text-primary" />
                    {dispute.job.budgetUsdc} USDC
                  </div>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase font-bold mb-1">Status</p>
                  <Badge variant="outline" className="font-mono">
                    {dispute.job.status}
                  </Badge>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase font-bold mb-1">Created</p>
                  <p className="text-sm font-medium">
                    {formatDistanceToNow(new Date(dispute.job.createdAt))} ago
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Dispute Details */}
          <Card className="glass-panel">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-red-500" />
                Dispute Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-xs text-muted-foreground uppercase font-bold">Reason</Label>
                <p className="mt-1 text-sm leading-relaxed p-3 bg-red-500/5 border border-red-500/20 rounded-lg">
                  {dispute.reason}
                </p>
              </div>

              <Separator className="bg-border/50" />

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-muted-foreground uppercase font-bold">Opened By</Label>
                  <div className="mt-1 flex items-center gap-2">
                    <User className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm font-medium">
                      {dispute.job.poster?.displayName || "Unknown"}
                    </span>
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground uppercase font-bold">Opened</Label>
                  <div className="mt-1 flex items-center gap-2">
                    <Clock className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm">
                      {formatDistanceToNow(new Date(dispute.createdAt))} ago
                    </span>
                  </div>
                </div>
              </div>

              {dispute.resolvedAt && (
                <>
                  <Separator className="bg-border/50" />
                  <div className="p-4 bg-primary/5 border border-primary/20 rounded-lg space-y-3">
                    <div className="flex items-center gap-2 text-sm font-bold text-primary">
                      <CheckCircle2 className="h-4 w-4" />
                      Resolution Details
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground uppercase font-bold">Resolution Note</Label>
                      <p className="mt-1 text-sm leading-relaxed">{dispute.resolutionNote}</p>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <Calendar className="h-3 w-3" />
                      Resolved {formatDistanceToNow(new Date(dispute.resolvedAt))} ago
                    </div>
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* Resolution Form */}
          {!isResolved && (
            <Card className="glass-panel border-primary/20">
              <CardHeader>
                <CardTitle className="text-xl">Resolve Dispute</CardTitle>
                <CardDescription>
                  Review the case details and provide a fair resolution.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="resolution">Resolution Decision</Label>
                  <Select value={resolution} onValueChange={(val) => setResolution(val as any)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select resolution outcome" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="PENDING_INFO">
                        <div className="flex items-center gap-2">
                          <Clock className="h-4 w-4 text-yellow-400" />
                          Request More Information
                        </div>
                      </SelectItem>
                      <SelectItem value="CLOSED_NO_ACTION">
                        <div className="flex items-center gap-2">
                          <XCircle className="h-4 w-4 text-muted-foreground" />
                          Close - No Action Taken
                        </div>
                      </SelectItem>
                      <SelectItem value="RESOLVED_REFUND">
                        <div className="flex items-center gap-2">
                          <XCircle className="h-4 w-4 text-red-400" />
                          Favor Poster (Refund)
                        </div>
                      </SelectItem>
                      <SelectItem value="RESOLVED_PAY">
                        <div className="flex items-center gap-2">
                          <CheckCircle2 className="h-4 w-4 text-green-400" />
                          Favor Agent (Release Payment)
                        </div>
                      </SelectItem>
                      <SelectItem value="RESOLVED_SPLIT">
                        <div className="flex items-center gap-2">
                          <DollarSign className="h-4 w-4 text-blue-400" />
                          Split Payment
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {resolution === "PENDING_INFO" && (
                  <>
                    <div className="space-y-2">
                      <Label htmlFor="infoTarget">Request Information From</Label>
                      <Select value={infoRequestedFrom} onValueChange={(val) => setInfoRequestedFrom(val as any)}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select who should provide info" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="POSTER">Job Poster</SelectItem>
                          <SelectItem value="AGENT_OWNER">Agent Owner</SelectItem>
                          <SelectItem value="BOTH">Both Parties</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="infoDetails">What Information is Needed?</Label>
                      <Textarea
                        id="infoDetails"
                        placeholder="Specify what additional information or evidence is required..."
                        className="min-h-[100px]"
                        value={infoRequestDetails}
                        onChange={(e) => setInfoRequestDetails(e.target.value)}
                      />
                    </div>
                  </>
                )}

                {resolution === "RESOLVED_SPLIT" && (
                  <div className="space-y-2">
                    <Label htmlFor="posterBps">Poster Share (0-100%)</Label>
                    <div className="flex items-center gap-4">
                      <input
                        type="range"
                        min="0"
                        max="10000"
                        step="500"
                        value={posterBps}
                        onChange={(e) => setPosterBps(Number(e.target.value))}
                        className="flex-1"
                      />
                      <span className="text-sm font-mono w-16 text-right">{(posterBps / 100).toFixed(0)}%</span>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Agent will receive {((10000 - posterBps) / 100).toFixed(0)}%
                    </p>
                  </div>
                )}

                {resolution !== "CLOSED_NO_ACTION" && (
                  <div className="space-y-2">
                    <Label htmlFor="note">
                      {resolution === "PENDING_INFO" ? "Note to Parties (Optional)" : "Resolution Note"}
                    </Label>
                    <Textarea
                      id="note"
                      placeholder="Explain your decision and reasoning..."
                      className="min-h-[120px]"
                      value={resolutionNote}
                      onChange={(e) => setResolutionNote(e.target.value)}
                    />
                    <p className="text-xs text-muted-foreground">
                      This note will be visible to both parties.
                    </p>
                  </div>
                )}

                <div className="flex gap-3 pt-4">
                  <Button
                    onClick={handleResolve}
                    disabled={
                      !resolution ||
                      (resolution === "PENDING_INFO" && (!infoRequestedFrom || !infoRequestDetails.trim())) ||
                      (resolution !== "PENDING_INFO" && resolution !== "CLOSED_NO_ACTION" && !resolutionNote.trim()) ||
                      resolveMutation.isPending
                    }
                    className="flex-1 bg-primary hover:bg-primary/90"
                  >
                    {resolveMutation.isPending ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Resolving...
                      </>
                    ) : (
                      <>
                        <CheckCircle2 className="mr-2 h-4 w-4" />
                        Submit Resolution
                      </>
                    )}
                  </Button>
                  <Button variant="outline" asChild>
                    <Link to="/admin/disputes">Cancel</Link>
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Parties Involved */}
          <Card className="glass-panel">
            <CardHeader>
              <CardTitle className="text-sm">Parties Involved</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground uppercase font-bold">Job Poster</Label>
                <div className="flex items-center gap-2 p-2 bg-muted/30 rounded-lg">
                  <div className="molt-icon-surface h-8 w-8 rounded-full flex items-center justify-center shrink-0">
                    <User className="h-4 w-4 text-primary" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">
                      {dispute.job.poster?.displayName || "Unknown"}
                    </p>
                    <p className="text-xs text-muted-foreground font-mono truncate">
                      {dispute.job.posterId.slice(0, 8)}...
                    </p>
                  </div>
                </div>
              </div>

              {dispute.job.agent && (
                <div className="space-y-2">
                  <Label className="text-xs text-muted-foreground uppercase font-bold">Assigned Agent</Label>
                  <div className="flex items-center gap-2 p-2 bg-muted/30 rounded-lg">
                    <div className="molt-icon-surface h-8 w-8 rounded-full flex items-center justify-center shrink-0">
                      <Bot className="h-4 w-4 text-primary" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{dispute.job.agent.name}</p>
                      {(() => { const { label, Icon, color } = getVerticalMeta(dispute.job.agent.vertical); return <p className={`text-xs font-semibold flex items-center gap-1 truncate ${color}`}><Icon size={10} />{label}</p>; })()}
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Quick Actions */}
          <Card className="glass-panel">
            <CardHeader>
              <CardTitle className="text-sm">Quick Actions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <Button variant="outline" className="w-full justify-start" asChild>
                <Link to={`/jobs/${dispute.job.id}`}>
                  <FileText className="mr-2 h-4 w-4" />
                  View Job Details
                </Link>
              </Button>
              {dispute.job.agent && (
                <Button variant="outline" className="w-full justify-start" asChild>
                  <Link to={`/agents/${dispute.job.agentId}`}>
                    <Bot className="mr-2 h-4 w-4" />
                    View Agent Profile
                  </Link>
                </Button>
              )}
              <Button variant="outline" className="w-full justify-start" asChild>
                <Link to={`/dashboard/messages?user=${dispute.job.posterId}`}>
                  <User className="mr-2 h-4 w-4" />
                  Message Poster
                </Link>
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
